'use client';

import { useMemo } from 'react';
import { useAiApplication } from '../../hooks/api/useAiApplication';
import { useMcpServerIdentities } from '../../hooks/api/useMcpServerIdentities';
import { usePermissions } from '../../hooks/usePermissions';
import {
  useIsMcpServerTarget,
  useProjectStore,
} from '../../store/projectStore';
import { resolveNeedsMcpDiscoveryAuth } from './ai-discovery-identity.util';

export function useAiDiscoveryCanRun({
  applicationId,
  enabled = true,
  isRunPending,
}: {
  applicationId: string;
  enabled?: boolean;
  isRunPending: boolean;
}): {
  canRun: boolean;
  isDraft: boolean;
  needsMcpAuth: boolean;
} {
  const { canManageApplications } = usePermissions();
  const isMcpServer = useIsMcpServerTarget();
  const isDraft = useProjectStore(
    (state) => state.selectedTarget?.isDraft === true,
  );
  const { aiApplication } = useAiApplication({
    aiApplicationId: applicationId,
    enabled: enabled && isMcpServer && Boolean(applicationId),
  });
  const mcpServerId = aiApplication?.mcpServerId ?? '';
  const { identities } = useMcpServerIdentities({
    serverId: enabled && isMcpServer ? mcpServerId : '',
  });
  const needsMcpAuth = useMemo(() => {
    if (!isMcpServer) {
      return false;
    }

    return resolveNeedsMcpDiscoveryAuth({ identities });
  }, [identities, isMcpServer]);

  return {
    canRun: canManageApplications && !isDraft && !needsMcpAuth && !isRunPending,
    isDraft,
    needsMcpAuth,
  };
}
